import type { AnswerDetailListItemResponse } from '@coorum-research/shared-common/api-response'
import type { MypageSettingResponse } from '@coorum-research/shared-common/api-response/mypage-setting.schema'
import type { MypageTagTab, SurveyAnswerRecord } from '@coorum-research/shared-common/types'
import type { TabStatisticsConfigDiscriminatedUnion } from '@coorum-research/shared-common/types/tag-statistics-configuration.type'
import type { ComponentProps, RefObject } from 'react'
import { Calendar } from '@coorum-research/core/src/components/Calendar'
import { MyPageAnswerList } from '@coorum-research/core/src/components/MyPageAnswerList'
import { StatCardsItem } from '@coorum-research/core/src/components/StatCards'
import { extractImageUrls } from '@coorum-research/shared-common/shared/survey/survey-image.util'
import { isTagTab } from '@coorum-research/shared-common/types/mypage-setting.type'
import { useQuery } from '@tanstack/react-query'
import { surveyApi } from '../apis/surveys.api'
import { userStatsApi } from '../apis/user-stats.api'
import { useCalendar } from '../composables/use-calendar'
import { addQueryParams, navigateInSameTab } from '../utils/url.util'
import { AnswersList } from './AnswersList'

const { getUserStats } = userStatsApi()
const { getSurveyWithConfig } = surveyApi()

type Props = {
  containerRef: RefObject<HTMLDivElement | null>
  headerTabMode: string
  mypageSetting: MypageSettingResponse | undefined
  isMypageSettingFetched: boolean
  setThumbnailSelectOpen: React.Dispatch<React.SetStateAction<boolean>>
  setAnswerImages: React.Dispatch<React.SetStateAction<string[]>>
  setSelectedTabUrl: React.Dispatch<React.SetStateAction<string | undefined>>
  setSelectedAnswer: React.Dispatch<React.SetStateAction<AnswerDetailListItemResponse | null>>
  handleAnswerClick: (answerId: string) => void
}

export function MyPageAnswerListContainer(props: Props) {
  const {
    headerTabMode,
    mypageSetting,
    isMypageSettingFetched,
    setThumbnailSelectOpen,
    setAnswerImages,
    setSelectedTabUrl,
    setSelectedAnswer,
    handleAnswerClick,
  } = props

  const tabItems = (mypageSetting?.configJson?.tabs ?? []).filter(({ enabled }) => enabled)

  const tagIds = tabItems.filter(isTagTab).map(({ tagId }) => tagId).filter((v): v is string => !!v)

  const { data: userStats } = useQuery({
    queryKey: ['get-user-stats', tagIds],
    queryFn: () => getUserStats(tagIds),
    refetchOnWindowFocus: false,
    enabled: isMypageSettingFetched && tagIds.length > 0,
  })

  const postAnswer = async (tab: MypageTagTab, answer: AnswerDetailListItemResponse) => {
    const survey = await getSurveyWithConfig(answer.surveyId)
    const surveyAnswer = answer.surveyAnswer ? JSON.parse(answer.surveyAnswer) as SurveyAnswerRecord : null

    if (survey.configJson && surveyAnswer) {
      const imageUrls = extractImageUrls(survey.configJson, surveyAnswer)

      if (tab.urlType === 'communitySns' && imageUrls.length > 0) {
        // SNSメニューかつ、回答データに画像がある場合は、モーダルを開いて画像を選択
        setSelectedTabUrl(tab.url)
        setSelectedAnswer(answer)
        setAnswerImages(imageUrls)
        setThumbnailSelectOpen(true)
      } else {
        // SNSメニュー以外・画像がない場合は、遷移先URLにresearch_answer_idを追加して新しいタブで開く
        const url = tab.url ? addQueryParams(tab.url, [{ key: 'research_answer_id', value: answer.id }]) : tab.url
        if (url && answer.answerStatus === 'done') {
          navigateInSameTab(url)
        }
      }
    } else {
      setAnswerImages([])
    }
  }

  const renderStatsContent: ComponentProps<typeof MyPageAnswerList>['renderStatsContent'] = (tab) => {
    const tagStats = (userStats ?? []).find(({ tagId }) => {
      return tagId === tab.tagId
    })
    const { label, value, config: statsConfig, rank } = tagStats ?? {}

    const data: Omit<TabStatisticsConfigDiscriminatedUnion, 'label' | 'numeratorNumber' | 'denominatorNumber'> & {
      label: string
      numeratorNumber: number
      denominatorNumber: number
    } = {
      ...statsConfig,
      label: label || '',
      numeratorNumber: rank?.numeratorNumber ?? 0,
      denominatorNumber: rank?.denominatorNumber ?? 0,
    }

    return (
      <StatCardsItem
        {...data}
      >
        {value}
      </StatCardsItem>
    )
  }

  const renderTabContent: ComponentProps<typeof MyPageAnswerList>['renderTabContent'] = (tab) => {
    // タブにURLが設定されている場合のみ、アイコンクリック時の遷移処理を定義
    // URLが設定されていない場合は undefined を返し、アイコンを非表示にする
    const handleIconClick = tab.url
      ? (item: AnswerDetailListItemResponse) => {
        void postAnswer(tab, item)
      }
      : undefined

    return (
      <AnswersList
        query={{ tagIds: [tab.tagId ?? ''] }}
        theme={mypageSetting?.configJson?.theme}
        onItemSelected={item => handleAnswerClick(item.id)}
        onItemIconClick={handleIconClick}
      />
    )
  }

  const renderOtherTabContent: ComponentProps<typeof MyPageAnswerList>['renderOtherTabContent'] = ({ tabs }) => {
    return (
      <AnswersList
        query={{ excludeTagIds: tabs.filter(isTagTab).map(({ tagId }) => tagId).filter((v): v is string => !!v) }}
        theme={mypageSetting?.configJson?.theme}
        onItemSelected={item => handleAnswerClick(item.id)}
      />
    )
  }

  const { yearMonth, dayCounts, dateAnswersMap, oldestAnswerDate, handleBack, handleForward, handleGetDateAnswers } = useCalendar()
  const renderCalendar = () => (
    <Calendar
      year={yearMonth.year}
      month={yearMonth.month}
      dayCounts={dayCounts}
      dateAnswersMap={dateAnswersMap}
      oldestAnswerDate={oldestAnswerDate}
      onBack={handleBack}
      onForward={handleForward}
      onDayClick={handleGetDateAnswers}
      onAnswerSelect={handleAnswerClick}
    />
  )

  return (
    <MyPageAnswerList
      tabs={tabItems}
      headerTabMode={headerTabMode}
      renderStatsContent={renderStatsContent}
      renderTabContent={renderTabContent}
      renderOtherTabContent={renderOtherTabContent}
      renderCalendar={renderCalendar}
    />
  )
}
